import React, { useEffect, useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import Loader from "../../Loader";
import LeaveFilters, { EMPTY_FILTERS } from "./LeaveFilters";
import LeaveStats from "./LeaveStats";
import LeaveTable from "./LeaveTable";
import LeaveApprovalModal from "./LeaveApprovalModal";
import LeaveFormModal from "./LeaveFormModal";
import LeaveHistoryModal from "./LeaveHistoryModal";
import PenaltyBreakdownModal from "./PenaltyBreakdownModal";
import {
  GET_LEAVES,
  PAGE_SIZE,
  STATS_LIMIT,
  computeLeaveStats,
  downloadCsv,
  toCsv,
} from "./leaveShared";

// The "List" view: filters, summary cards (one employee + a date range),
// the paged table, and the per-leave modals.

const EXPORT_PAGE = 200;

const cleanFilters = (filters) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

const LeaveListView = ({ employees, leaveTypeOptions, focusLeaveId }) => {
  const client = useApolloClient();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [reviewing, setReviewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [breakdownOf, setBreakdownOf] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Opened from a link to one leave: open its review popup
  const { data: focusData } = useQuery(GET_LEAVES, {
    variables: { data: { id: focusLeaveId, limit: 1 } },
    fetchPolicy: "network-only",
    skip: !focusLeaveId,
  });
  useEffect(() => {
    const focused = focusData?.getAdminLeaveRequests?.leaves?.[0];
    if (focused) setReviewing(focused);
  }, [focusData]);

  // Wait for both ends of a date range
  const halfRange = Boolean(filters.startDate) !== Boolean(filters.endDate);

  const { data, loading } = useQuery(GET_LEAVES, {
    variables: {
      data: { ...cleanFilters(filters), offset: page * PAGE_SIZE, limit: PAGE_SIZE },
    },
    fetchPolicy: "network-only",
    skip: halfRange,
  });
  const leaves = data?.getAdminLeaveRequests?.leaves || [];
  const total = data?.getAdminLeaveRequests?.total || 0;

  // Summary cards need every leave of the employee in the range, not just
  // the visible page.
  const statsMode = Boolean(filters.employeeId && filters.startDate && filters.endDate);
  const { data: statsData } = useQuery(GET_LEAVES, {
    variables: {
      data: {
        employeeId: filters.employeeId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        offset: 0,
        limit: STATS_LIMIT,
      },
    },
    fetchPolicy: "network-only",
    skip: !statsMode,
  });
  const stats = useMemo(
    () =>
      statsMode
        ? computeLeaveStats(
            statsData?.getAdminLeaveRequests?.leaves || [],
            filters.employeeId,
            filters.startDate,
            filters.endDate,
          )
        : null,
    [statsMode, statsData, filters],
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = [];
      for (let offset = 0; ; offset += EXPORT_PAGE) {
        // eslint-disable-next-line no-await-in-loop
        const res = await client.query({
          query: GET_LEAVES,
          variables: { data: { ...cleanFilters(filters), offset, limit: EXPORT_PAGE } },
          fetchPolicy: "network-only",
        });
        const pageRows = res.data?.getAdminLeaveRequests?.leaves || [];
        rows.push(...pageRows);
        if (pageRows.length < EXPORT_PAGE) break;
      }
      const csv = toCsv(rows, [
        ["Employee", (l) => l.employee?.fullName],
        ["Phone", (l) => l.employee?.contactNumber],
        ["Type", (l) => l.leaveType],
        ["Mode", (l) => l.leaveMode],
        ["Start", (l) => l.startDate],
        ["End", (l) => l.endDate],
        ["Status", (l) => l.status],
        ["Reason", (l) => l.reason],
        ["Admin remark", (l) => l.adminRemark],
        ["Fine (₹)", (l) => (l.status === "PENDING" ? "" : l.penaltyAmount)],
        ["Projected fine (₹)", (l) => (l.status === "PENDING" ? l.projectedPenaltyAmount : "")],
        ["Document", (l) => (l.hasDocument ? "Attached" : l.documentRequired ? "Missing" : "")],
        ["Applied", (l) => (l.createdAt ? new Date(l.createdAt).toISOString().slice(0, 10) : "")],
      ]);
      const range = filters.startDate ? `${filters.startDate}_${filters.endDate}` : "all";
      downloadCsv(`leave-requests_${range}.csv`, csv);
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <LeaveFilters filters={filters} onChange={setFilters} employees={employees} />

      {stats && <LeaveStats stats={stats} />}

      <div className="flex justify-end mb-2">
        <button
          onClick={exportCsv}
          disabled={exporting || total === 0}
          className="text-sm px-3 py-1.5 border rounded bg-white hover:bg-gray-100 disabled:opacity-40"
        >
          {exporting ? "Exporting..." : `Export ${total} as CSV`}
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <LeaveTable
          leaves={leaves}
          total={total}
          page={page}
          onPage={setPage}
          onReview={setReviewing}
          onHistory={setHistoryOf}
          onEdit={setEditing}
          onBreakdown={setBreakdownOf}
        />
      )}

      {reviewing && (
        <LeaveApprovalModal leave={reviewing} onClose={() => setReviewing(null)} />
      )}
      {editing && (
        <LeaveFormModal
          leave={editing}
          employees={employees}
          leaveTypeOptions={leaveTypeOptions}
          onClose={() => setEditing(null)}
        />
      )}
      {historyOf && <LeaveHistoryModal leave={historyOf} onClose={() => setHistoryOf(null)} />}
      {breakdownOf && (
        <PenaltyBreakdownModal leave={breakdownOf} onClose={() => setBreakdownOf(null)} />
      )}
    </>
  );
};

export default LeaveListView;
